/**
 * Hostname-Based Portal Routing Utility
 *
 * 1. app.fundelitehq.com = Platform HQ (internal platform console)
 * 2. app.fundelite.ai = Product app (agency + self-repair + managed clients)
 * 3. fundelite.ai / www.fundelite.ai = FundElite public marketing
 * 4. fundgeniushq.com / www.fundgeniushq.com = FundGenius HQ public marketing
 * 5. app.fundgeniushq.com = FundGenius HQ authenticated app portal
 * 6. intake.fundgeniushq.com = FundGenius HQ funding intake portal
 * 7. Preview/Dev environments allow all routes for testing
 */

export const AGENCY_APP_URL =
  process.env.REACT_APP_AGENCY_APP_URL ||
  process.env.REACT_APP_SELF_REPAIR_APP_URL ||
  'https://app.fundelite.ai';

export const PLATFORM_APP_URL =
  process.env.REACT_APP_PLATFORM_APP_URL ||
  process.env.REACT_APP_ADMIN_AGENCY_APP_URL ||
  'https://app.fundelitehq.com';

export const FUNDGENIUS_MARKETING_URL =
  process.env.REACT_APP_FUNDGENIUS_MARKETING_URL || 'https://fundgeniushq.com';

export const FUNDGENIUS_APP_URL =
  process.env.REACT_APP_FUNDGENIUS_APP_URL || 'https://app.fundgeniushq.com';

export const FUNDGENIUS_INTAKE_URL =
  process.env.REACT_APP_FUNDGENIUS_INTAKE_URL || 'https://intake.fundgeniushq.com';

// Portal types
export const HOSTNAME_PORTALS = {
  ADMIN: 'admin',
  CUSTOMER: 'customer',
  MARKETING: 'marketing',
  FUNDGENIUS_MARKETING: 'fundgenius_marketing',
  FUNDGENIUS_APP: 'fundgenius_app',
  FUNDGENIUS_INTAKE: 'fundgenius_intake',
  DEV: 'dev',
};

const DEFAULT_MARKETING_HOSTS = ['fundelite.ai', 'www.fundelite.ai'];

const FUNDGENIUS_MARKETING_HOSTS = ['fundgeniushq.com', 'www.fundgeniushq.com'];

const normalizeHostname = (hostname = window.location.hostname) =>
  String(hostname || '').toLowerCase().split(':')[0];

const getConfiguredMarketingHosts = () => {
  const configured = (process.env.REACT_APP_MARKETING_HOSTS || '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set([...DEFAULT_MARKETING_HOSTS, ...configured])];
};

export const isMarketingHostname = (hostname = window.location.hostname) => {
  const normalizedHost = normalizeHostname(hostname);
  if (normalizedHost.startsWith('app.')) {
    return false;
  }
  if (FUNDGENIUS_MARKETING_HOSTS.includes(normalizedHost)) {
    return false;
  }
  return getConfiguredMarketingHosts().includes(normalizedHost);
};

export const isFundGeniusMarketingHostname = (hostname = window.location.hostname) => {
  return FUNDGENIUS_MARKETING_HOSTS.includes(normalizeHostname(hostname));
};

export const isFundGeniusAppHostname = (hostname = window.location.hostname) => {
  return normalizeHostname(hostname) === 'app.fundgeniushq.com';
};

export const isFundGeniusIntakeHostname = (hostname = window.location.hostname) => {
  return normalizeHostname(hostname) === 'intake.fundgeniushq.com';
};

// Domain to portal mapping
const HOSTNAME_PORTAL_MAP = {
  'app.fundelitehq.com': HOSTNAME_PORTALS.ADMIN,
  'team.fundelitehq.com': HOSTNAME_PORTALS.ADMIN,
  'app.fundelite.ai': HOSTNAME_PORTALS.CUSTOMER,
  'fundgeniushq.com': HOSTNAME_PORTALS.FUNDGENIUS_MARKETING,
  'www.fundgeniushq.com': HOSTNAME_PORTALS.FUNDGENIUS_MARKETING,
  'app.fundgeniushq.com': HOSTNAME_PORTALS.FUNDGENIUS_APP,
  'intake.fundgeniushq.com': HOSTNAME_PORTALS.FUNDGENIUS_INTAKE,
};

// Admin portal allowed roles (app.fundelitehq.com) - STAFF/ADMIN ONLY
export const ADMIN_PORTAL_ROLES = [
  'super_admin',
  'platform_admin', 
  'platform_owner',
  'admin',
  'owner',
  'staff',
  'staff_admin',
  'support',
  'specialist',
];

// Platform Console roles (default /platform/dashboard, PlatformOwnerLayout)
export const PLATFORM_CONSOLE_ROLES = [
  'platform_owner',
  'super_admin',
  'platform_admin',
];

// HQ staff/agency operations (default /staff/dashboard, StaffPortalLayout)
export const HQ_STAFF_OPERATIONS_ROLES = [
  'admin',
  'owner',
  'staff',
  'staff_admin',
  'support',
  'specialist',
];

// Customer portal allowed roles (app.fundelite.ai) - Agency + Self-Repair
export const CUSTOMER_PORTAL_ROLES = [
  // Agency roles
  'agency_owner',
  'agency_admin',
  'agency_staff',
  'agency_user',
  // Self-repair roles
  'self_repair_customer',
  'self_repair',
  'client',
  'customer',
  'managed_client',
];

// Agency-specific roles (for routing to /agency/dashboard)
export const AGENCY_ROLES = [
  'agency_owner',
  'agency_admin',
  'agency_staff',
  'agency_user',
];

// Self-repair roles (for routing to onboarding/dashboard)
export const SELF_REPAIR_ROLES = [
  'self_repair_customer',
  'self_repair',
  'self_repair_user',
  'premium_user',
  'client',
  'customer',
  'managed_client',
];

/**
 * Detect portal type from hostname
 */
export function getHostnamePortal(hostname = window.location.hostname) {
  const normalizedHost = normalizeHostname(hostname);

  if (HOSTNAME_PORTAL_MAP[normalizedHost]) {
    return HOSTNAME_PORTAL_MAP[normalizedHost];
  }

  if (isMarketingHostname(normalizedHost)) {
    return HOSTNAME_PORTALS.MARKETING;
  }

  // Check patterns
  if (normalizedHost.includes('fundelitehq')) {
    return HOSTNAME_PORTALS.ADMIN;
  }

  if (normalizedHost.includes('fundgeniushq')) {
    if (normalizedHost.startsWith('app.')) {
      return HOSTNAME_PORTALS.FUNDGENIUS_APP;
    }
    if (normalizedHost.startsWith('intake.')) {
      return HOSTNAME_PORTALS.FUNDGENIUS_INTAKE;
    }
    return HOSTNAME_PORTALS.FUNDGENIUS_MARKETING;
  }

  if (normalizedHost.includes('fundelite.ai')) {
    return HOSTNAME_PORTALS.CUSTOMER;
  }

  // Dev/preview environments
  if (normalizedHost === 'localhost' ||
      normalizedHost.includes('127.0.0.1') ||
      normalizedHost.includes('preview.emergentagent.com') ||
      normalizedHost.includes('emergent.host') ||
      normalizedHost.includes('.vercel.app')) {
    return HOSTNAME_PORTALS.DEV;
  }

  // Default to customer for unknown domains
  return HOSTNAME_PORTALS.CUSTOMER;
}

export const PORTAL_SURFACES = {
  MARKETING: 'marketing',
  SELF_REPAIR: 'self_repair',
  ADMIN_AGENCY: 'admin_agency',
  FUNDGENIUS_MARKETING: 'fundgenius_marketing',
  FUNDGENIUS_APP: 'fundgenius_app',
  FUNDGENIUS_INTAKE: 'fundgenius_intake',
  DEVELOPMENT: 'development',
};

export function getPortalFromHostname(hostname = window.location.hostname) {
  const portal = getHostnamePortal(hostname);
  if (portal === HOSTNAME_PORTALS.MARKETING) {
    return PORTAL_SURFACES.MARKETING;
  }
  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING) {
    return PORTAL_SURFACES.FUNDGENIUS_MARKETING;
  }
  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
    return PORTAL_SURFACES.FUNDGENIUS_APP;
  }
  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_INTAKE) {
    return PORTAL_SURFACES.FUNDGENIUS_INTAKE;
  }
  if (portal === HOSTNAME_PORTALS.ADMIN) {
    return PORTAL_SURFACES.ADMIN_AGENCY;
  }
  if (portal === HOSTNAME_PORTALS.CUSTOMER) {
    return PORTAL_SURFACES.SELF_REPAIR;
  }
  if (portal === HOSTNAME_PORTALS.DEV) {
    return PORTAL_SURFACES.DEVELOPMENT;
  }
  return PORTAL_SURFACES.SELF_REPAIR;
}

export const HQ_STAFF_ROLES = HQ_STAFF_OPERATIONS_ROLES;

export const isPlatformConsoleRole = (role) =>
  PLATFORM_CONSOLE_ROLES.includes(String(role || '').toLowerCase());

export const isHqStaffRole = (role) =>
  HQ_STAFF_OPERATIONS_ROLES.includes(String(role || '').toLowerCase());

export const getAppHostTargets = () => ({
  selfRepairHost: AGENCY_APP_URL,
  agencyAppHost: AGENCY_APP_URL,
  adminAgencyHost: PLATFORM_APP_URL,
  platformAppHost: PLATFORM_APP_URL,
  fundGeniusMarketingHost: FUNDGENIUS_MARKETING_URL,
  fundGeniusAppHost: FUNDGENIUS_APP_URL,
  fundGeniusIntakeHost: FUNDGENIUS_INTAKE_URL,
  marketingHost: process.env.REACT_APP_MARKETING_APP_URL || 'https://fundelite.ai',
});

/**
 * Detect Vercel preview / local non-production app hosts.
 * Preview must never bounce to production app.fundelite.ai.
 */
export function isPreviewDeploymentHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const normalizedHost = normalizeHostname(hostname);
  if (normalizedHost.endsWith('.vercel.app')) {
    return true;
  }
  const vercelEnv = String(
    process.env.REACT_APP_VERCEL_ENV
    || process.env.VERCEL_ENV
    || ''
  ).toLowerCase();
  return vercelEnv === 'preview';
}

/** True for localhost, emergent previews, and Vercel previews. */
export function isNonProductionAppHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  return getHostnamePortal(hostname) === HOSTNAME_PORTALS.DEV || isPreviewDeploymentHost(hostname);
}

export function stayOnCurrentOriginUrl(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${normalized}`;
  }
  return normalized;
}

export const getFundGeniusAppUrl = (path = '/login') => {
  if (isNonProductionAppHost()) {
    return stayOnCurrentOriginUrl(path);
  }
  const { fundGeniusAppHost } = getAppHostTargets();
  return `${fundGeniusAppHost}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getFundGeniusIntakeUrl = (path = '/') => {
  if (isNonProductionAppHost()) {
    return stayOnCurrentOriginUrl(path);
  }
  const { fundGeniusIntakeHost } = getAppHostTargets();
  return `${fundGeniusIntakeHost}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getPlatformHqUrl = (path = '/platform/dashboard') => {
  if (isNonProductionAppHost()) {
    return stayOnCurrentOriginUrl(path);
  }
  const { adminAgencyHost } = getAppHostTargets();
  return `${adminAgencyHost}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getProductAppUrl = (path = '/dashboard') => {
  if (isNonProductionAppHost()) {
    return stayOnCurrentOriginUrl(path);
  }
  const { selfRepairHost } = getAppHostTargets();
  return `${selfRepairHost}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Check if current hostname is admin portal
 */
export function isAdminPortalHostname() {
  return getHostnamePortal() === HOSTNAME_PORTALS.ADMIN;
}

/**
 * Check if current hostname is customer portal
 */
export function isCustomerPortalHostname() {
  return getHostnamePortal() === HOSTNAME_PORTALS.CUSTOMER;
}

/**
 * Check if current hostname is dev/preview
 */
export function isDevHostname() {
  return getHostnamePortal() === HOSTNAME_PORTALS.DEV;
}

/**
 * Check if a role is allowed on the current hostname portal
 */
export function isRoleAllowedOnHostname(role, hostname = window.location.hostname) {
  if (!role) return false;
  
  const portal = getHostnamePortal(hostname);
  const normalizedRole = role.toLowerCase();
  
  // Dev mode allows all
  if (portal === HOSTNAME_PORTALS.DEV) return true;

  if (portal === HOSTNAME_PORTALS.MARKETING || portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING) {
    return false;
  }

  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_INTAKE) {
    return true;
  }

  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
    return ADMIN_PORTAL_ROLES.includes(normalizedRole) || AGENCY_ROLES.includes(normalizedRole);
  }
  
  if (portal === HOSTNAME_PORTALS.ADMIN) {
    return ADMIN_PORTAL_ROLES.includes(normalizedRole);
  }
  
  if (portal === HOSTNAME_PORTALS.CUSTOMER) {
    return CUSTOMER_PORTAL_ROLES.includes(normalizedRole);
  }
  
  return false;
}

/**
 * Get redirect URL for wrong-portal scenario
 */
export function getCorrectPortalRedirect(role, user = null) {
  if (!role && !user) return null;

  const normalizedRole = String(role || user?.role || '').toLowerCase();
  const currentPortal = getHostnamePortal();

  const roleLower = String(role || user?.role || '').toLowerCase();
  const hasAgencyId = user?.agency_id && user.agency_id !== 'default_agency';
  const isClientRole = SELF_REPAIR_ROLES.includes(roleLower);
  const agencyWorkspaceUser =
    user &&
    !isClientRole &&
    !isPlatformConsoleRole(roleLower) &&
    !['staff', 'staff_admin', 'support', 'specialist'].includes(roleLower) &&
    (AGENCY_ROLES.includes(roleLower) ||
      String(user.portal || '').toLowerCase() === 'agency' ||
      String(user.account_type || '').toLowerCase() === 'agency' ||
      ((roleLower === 'owner' || roleLower === 'admin') &&
        hasAgencyId &&
        user.platform_access !== true));

  if (currentPortal === HOSTNAME_PORTALS.DEV) return null;

  if (
    currentPortal === HOSTNAME_PORTALS.CUSTOMER &&
    ADMIN_PORTAL_ROLES.includes(normalizedRole) &&
    !agencyWorkspaceUser
  ) {
    const adminDashboard = isPlatformConsoleRole(normalizedRole)
      ? '/platform/dashboard'
      : '/staff/dashboard';
    return getPlatformHqUrl(adminDashboard);
  }
  
  // Agency/Self-repair role on admin portal -> redirect to customer portal
  if (
    currentPortal === HOSTNAME_PORTALS.ADMIN &&
    (CUSTOMER_PORTAL_ROLES.includes(normalizedRole) || agencyWorkspaceUser)
  ) {
    // Agency users go to agency dashboard when assigned
    if ((AGENCY_ROLES.includes(normalizedRole) || agencyWorkspaceUser) && hasAgencyId) {
      return getProductAppUrl('/agency/dashboard');
    }
    return getProductAppUrl('/dashboard');
  }
  
  return null;
}

/**
 * Get the default landing route for a given portal
 */
export function getPortalDefaultRoute(portal = getHostnamePortal()) {
  switch (portal) {
    case HOSTNAME_PORTALS.ADMIN:
      return '/platform/dashboard';
    case HOSTNAME_PORTALS.FUNDGENIUS_APP:
      return '/platform/fundgenius-hq';
    case HOSTNAME_PORTALS.FUNDGENIUS_INTAKE:
      return '/';
    case HOSTNAME_PORTALS.FUNDGENIUS_MARKETING:
    case HOSTNAME_PORTALS.MARKETING:
      return '/';
    case HOSTNAME_PORTALS.CUSTOMER:
      return '/dashboard';
    default:
      return '/dashboard';
  }
}

/**
 * Get the login route for a given portal
 */
export function getPortalLoginRoute(portal = getHostnamePortal()) {
  return '/login';
}

/**
 * Check if a route is allowed on the current hostname portal
 */
export function isRouteAllowedOnHostname(pathname, hostname = window.location.hostname) {
  const portal = getHostnamePortal(hostname);
  
  // Dev allows all routes
  if (portal === HOSTNAME_PORTALS.DEV) return true;

  if (portal === HOSTNAME_PORTALS.MARKETING) {
    const marketingAllowedRoutes = ['/', '/pricing', '/features', '/login', '/signup'];
    return marketingAllowedRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  }

  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING) {
    const fundGeniusMarketingRoutes = [
      '/',
      '/pricing',
      '/features',
      '/signup',
      '/login',
      '/forgot-password',
      '/onboarding',
    ];
    return fundGeniusMarketingRoutes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );
  }

  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_INTAKE) {
    return (
      pathname === '/'
      || pathname.startsWith('/intake')
      || /^\/[^/]+(\/start)?$/.test(pathname)
      || /^\/[^/]+\/client\/[^/]+$/.test(pathname)
    );
  }

  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
    const fundGeniusAppRoutes = [
      '/login',
      '/signin',
      '/signup',
      '/forgot-password',
      '/reset-password',
      '/verify-email',
      '/dashboard',
      '/fundgenius-hq',
      '/platform/fundgenius-hq',
    ];
    return fundGeniusAppRoutes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );
  }
  
  // Platform HQ blocked routes (product-app flows unless support mode is handled upstream)
  const adminBlockedRoutes = ['/onboarding', '/signup', '/trial'];
  
  // Customer portal blocked routes (admin-only flows)
  const customerBlockedRoutes = ['/platform', '/staff'];
  
  if (portal === HOSTNAME_PORTALS.ADMIN) {
    return !adminBlockedRoutes.some(route => pathname.startsWith(route));
  }
  
  if (portal === HOSTNAME_PORTALS.CUSTOMER) {
    return !customerBlockedRoutes.some(route => pathname.startsWith(route));
  }
  
  return true;
}

/**
 * Get dashboard path based on role and portal.
 * When user is provided, agency workspace owners always win over generic owner/admin staff routing.
 */
export function getDashboardForRoleOnPortal(role, portal = getHostnamePortal(), user = null) {
  if (!role && !user) return '/dashboard';

  if (user) {
    const agencyRole = String(user.role || '').toLowerCase();
    const agencyId = user.agency_id;
    const agencyWorkspaceRoles = new Set([
      'agency_owner',
      'agency_admin',
      'agency_staff',
      'agency_user',
    ]);

    if (isPlatformConsoleRole(agencyRole)) {
      return '/platform/dashboard';
    }

    if (['staff', 'staff_admin', 'support', 'specialist'].includes(agencyRole)) {
      return '/staff/dashboard';
    }

    if (agencyWorkspaceRoles.has(agencyRole)) {
      return agencyId && agencyId !== 'default_agency' ? '/agency/dashboard' : '/dashboard';
    }
    if ((agencyRole === 'owner' || agencyRole === 'admin') && agencyId && agencyId !== 'default_agency') {
      return '/agency/dashboard';
    }
    if ((agencyRole === 'owner' || agencyRole === 'admin') && user.platform_access === true) {
      return agencyRole === 'owner' ? '/staff/dashboard' : '/staff/dashboard';
    }
  }

  if (!role) return '/dashboard';

  const normalizedRole = role.toLowerCase();
  
  // Platform-level admins
  if (isPlatformConsoleRole(normalizedRole)) {
    return '/platform/dashboard';
  }
  
  // Staff/Admin users — never route bare owner/admin to staff when agency context exists
  if (['admin', 'owner', 'staff', 'staff_admin', 'support', 'specialist'].includes(normalizedRole)) {
    if (user && user.agency_id && user.agency_id !== 'default_agency') {
      return '/agency/dashboard';
    }
    return '/staff/dashboard';
  }
  
  // Agency users
  if (AGENCY_ROLES.includes(normalizedRole)) {
    if (user && user.agency_id && user.agency_id !== 'default_agency') {
      return '/agency/dashboard';
    }
    return '/dashboard';
  }
  
  // Managed clients
  if (normalizedRole === 'managed_client') {
    return '/client/dashboard';
  }
  
  // Self-repair customers
  return '/dashboard';
}

export default {
  HOSTNAME_PORTALS,
  ADMIN_PORTAL_ROLES,
  CUSTOMER_PORTAL_ROLES,
  AGENCY_ROLES,
  SELF_REPAIR_ROLES,
  getHostnamePortal,
  isAdminPortalHostname,
  isCustomerPortalHostname,
  isDevHostname,
  isRoleAllowedOnHostname,
  getCorrectPortalRedirect,
  getPortalDefaultRoute,
  getPortalLoginRoute,
  isRouteAllowedOnHostname,
  getDashboardForRoleOnPortal,
};
